import React, { useState, useEffect, createContext, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useMeeting } from "../App";
import NotesPanel from "./NotesPanel";
import ScenarioSelectionModal from "./ScenarioSelectionModal";
import axios from "axios";
import {
  LayoutDashboard,
  Zap,
  Settings2,
  Cpu,
  Cable,
  FileText,
  LogOut,
  User,
  ChevronRight,
  Home,
  Presentation,
  X,
  ChevronLeft,
  Maximize2,
  ClipboardList,
  Building2,
  Users,
  Briefcase,
  Shield,
  ExternalLink,
  Settings
} from "lucide-react";
import { Button } from "./ui/button";

// Presentation Mode Context
const PresentationContext = createContext(null);

export const usePresentationMode = () => useContext(PresentationContext);

export const PresentationProvider = ({ children }) => {
  const [isPresentationMode, setIsPresentationMode] = useState(false);

  const enterPresentationMode = () => setIsPresentationMode(true);
  const exitPresentationMode = () => setIsPresentationMode(false);
  const togglePresentationMode = () => setIsPresentationMode(!isPresentationMode);

  // Handle ESC key to exit presentation mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isPresentationMode) {
        exitPresentationMode();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPresentationMode]);

  return (
    <PresentationContext.Provider
      value={{
        isPresentationMode,
        enterPresentationMode,
        exitPresentationMode,
        togglePresentationMode
      }}
    >
      {children}
    </PresentationContext.Provider>
  );
};

// Floating Navigation for Presentation Mode
const PresentationNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { exitPresentationMode } = usePresentationMode();
  const { currentMeeting } = useMeeting();
  const { token } = useAuth();
  const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  // Fetch branding for presentation mode
  useEffect(() => {
    const fetchBranding = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/branding/current`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setBranding(response.data);
        } catch (error) {
          console.error("Failed to fetch branding:", error);
        }
      }
    };
    fetchBranding();
  }, [token]);

  const handleSelectScenario = (scenario) => {
    // For custom scenarios, store in sessionStorage so presentation page can retrieve it
    if (scenario.isCustom) {
      sessionStorage.setItem('customScenario', JSON.stringify(scenario));
    }
    // Pass current location so scenario can return here
    navigate(`/scenario/${scenario.id}?returnTo=${encodeURIComponent(location.pathname)}`);
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/assets/transformers", label: "Transformers", icon: Zap },
    { path: "/assets/switchgear", label: "Switchgear", icon: Settings2 },
    { path: "/assets/motors", label: "Motors", icon: Cpu },
    { path: "/assets/cables", label: "Cables", icon: Cable },
    { path: "/summary", label: "Summary", icon: FileText }
  ];

  const currentIndex = navItems.findIndex(item => item.path === location.pathname);
  const prevItem = currentIndex > 0 ? navItems[currentIndex - 1] : null;
  const nextItem = currentIndex < navItems.length - 1 ? navItems[currentIndex + 1] : null;
  const currentItem = navItems[currentIndex];

  // Page titles for presentation
  const pageTitles = {
    "/dashboard": "Asset Dashboard",
    "/assets/transformers": "Transformer Condition Monitoring",
    "/assets/switchgear": "Switchgear Condition Monitoring",
    "/assets/motors": "Motor Condition Monitoring",
    "/assets/cables": "Cable Condition Monitoring",
    "/summary": "Session Summary"
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft" && prevItem) {
        navigate(prevItem.path);
      } else if (e.key === "ArrowRight" && nextItem) {
        navigate(nextItem.path);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevItem, nextItem, navigate]);

  return (
    <>
      {/* Top Bar - Branding, Title, Session Info & Exit */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm text-white px-6 py-2.5 flex items-center justify-between"
        style={{ 
          backgroundColor: branding?.primary_color ? `${branding.primary_color}f5` : 'rgba(15, 23, 42, 0.95)'
        }}
      >
        <div className="flex items-center gap-6">
          {/* Company Branding */}
          <div className="flex items-center gap-3">
            {branding?.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt={branding.company_name || "Company Logo"} 
                className="h-8 w-auto object-contain"
              />
            ) : (
              <div 
                className="w-8 h-8 rounded-md flex items-center justify-center"
                style={{ backgroundColor: branding?.accent_color || '#3B82F6' }}
              >
                <Zap className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-semibold text-sm leading-tight">
                {branding?.company_name || "Asset Atlas"}
              </span>
              {branding?.website_url && (
                <a 
                  href={branding.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1 transition-opacity"
                >
                  {branding.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
          
          {/* Page Title */}
          <div className="border-l border-white/20 pl-6 flex items-center gap-2">
            {currentItem && <currentItem.icon className="w-5 h-5 opacity-80" />}
            <span className="font-medium">{pageTitles[location.pathname] || "Asset Atlas"}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Page indicator dots */}
          <div className="flex items-center gap-1.5">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`h-2 rounded-full transition-all ${
                  location.pathname === item.path 
                    ? 'bg-white w-6' 
                    : 'bg-white/30 hover:bg-white/50 w-2'
                }`}
                title={item.label}
              />
            ))}
          </div>
          
          <span className="opacity-30">|</span>
          
          {/* Scenarios Button */}
          <button
            onClick={() => setIsScenarioModalOpen(true)}
            className="flex items-center gap-1.5 opacity-70 hover:opacity-100 transition-opacity text-sm px-2 py-1 rounded hover:bg-white/10"
            data-testid="scenarios-btn"
          >
            <ClipboardList className="w-4 h-4" />
            Scenarios
          </button>
          
          <span className="opacity-30">|</span>
          
          <button
            onClick={exitPresentationMode}
            className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity text-sm"
            data-testid="exit-presentation-btn"
          >
            <X className="w-4 h-4" />
            Exit
          </button>
        </div>
      </div>

      {/* Scenario Selection Modal */}
      <ScenarioSelectionModal 
        isOpen={isScenarioModalOpen} 
        onClose={() => setIsScenarioModalOpen(false)}
        onSelectScenario={handleSelectScenario}
      />

      {/* Bottom Navigation Bar - Compact */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm px-6 py-2">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          {/* Previous */}
          <div className="w-40">
            {prevItem ? (
              <button
                onClick={() => navigate(prevItem.path)}
                className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm"
                data-testid="prev-nav-btn"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>{prevItem.label}</span>
              </button>
            ) : (
              <div />
            )}
          </div>

          {/* Center - Keyboard hint */}
          <span className="text-slate-500 text-xs">
            ← → arrow keys to navigate • ESC to exit
          </span>

          {/* Next */}
          <div className="w-40 flex justify-end">
            {nextItem ? (
              <button
                onClick={() => navigate(nextItem.path)}
                className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors text-sm"
                data-testid="next-nav-btn"
              >
                <span>{nextItem.label}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { currentMeeting, clearMeeting } = useMeeting();
  const { isPresentationMode, enterPresentationMode } = usePresentationMode();

  // For Master Admin, show only admin-related navigation
  const isMasterAdmin = user?.is_master_admin;

  // Regular user navigation items (not shown to Master Admin)
  const navItems = isMasterAdmin ? [] : [
    { path: "/sessions", label: "Sessions", icon: ClipboardList },
    { path: "/dashboard", label: "Asset Dashboard", icon: LayoutDashboard },
    { path: "/assets/transformers", label: "Transformers", icon: Zap },
    { path: "/assets/switchgear", label: "Switchgear", icon: Settings2 },
    { path: "/assets/motors", label: "Motors", icon: Cpu },
    { path: "/assets/cables", label: "Cables", icon: Cable },
    { path: "/summary", label: "Summary", icon: FileText },
    { path: "/customers", label: "Customers", icon: Briefcase }
  ];

  // Team management for org admins/owners (not for Master Admin)
  const teamNavItems = (!isMasterAdmin && (user?.organization_role === 'owner' || user?.organization_role === 'admin')) ? [
    { path: "/team", label: "Team", icon: Users }
  ] : [];

  // Admin nav items (only for Master Admin)
  const adminNavItems = isMasterAdmin ? [
    { path: "/admin", label: "Admin Dashboard", icon: Shield },
    { path: "/admin/assets", label: "Asset Configuration", icon: Settings }
  ] : [];

  const handleLogout = () => {
    clearMeeting();
    logout();
    navigate("/login");
  };

  const getBreadcrumbs = () => {
    const pathMap = {
      "/sessions": "Session History",
      "/dashboard": "Asset Dashboard",
      "/assets/transformers": "Transformers",
      "/assets/switchgear": "Switchgear",
      "/assets/motors": "Motors",
      "/assets/cables": "Cables",
      "/summary": "Summary",
      "/organization": "Organization",
      "/customers": "Customers",
      "/team": "Team",
      "/admin": "Admin Dashboard"
    };
    return pathMap[location.pathname] || "Dashboard";
  };

  // Presentation Mode Layout
  if (isPresentationMode) {
    return (
      <div className="min-h-screen bg-slate-50 pt-14 pb-12">
        <PresentationNav />
        <main className="px-6 py-4 max-w-7xl mx-auto">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
        {currentMeeting && <NotesPanel />}
      </div>
    );
  }

  // Normal Layout
  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="sidebar-header">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-white font-semibold text-heading text-lg">Asset Atlas</h1>
              <p className="text-slate-400 text-xs">CBM Diagnostic Engine</p>
            </div>
          </Link>
        </div>

        {/* Current Session Info - not for Master Admin */}
        {!isMasterAdmin && currentMeeting && (
          <div className="px-4 py-3 border-b border-slate-800">
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Current Session</p>
            <p className="text-white font-medium text-sm truncate">{currentMeeting.customer_name}</p>
            <p className="text-slate-400 text-xs truncate">{currentMeeting.company}</p>
          </div>
        )}

        {/* Presentation Mode Button - not for Master Admin */}
        {!isMasterAdmin && (
          <div className="px-4 py-3 border-b border-slate-800">
            <Button
              onClick={enterPresentationMode}
              className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="enter-presentation-btn"
            >
              <Presentation className="w-4 h-4" />
              Present Mode
            </Button>
          </div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, "-")}`}
                className={`sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </Link>
            );
          })}
          
          {/* Team Management - for org admins/owners */}
          {teamNavItems.length > 0 && (
            <>
              <div className="border-t border-slate-800/50 my-2 pt-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 px-3">Management</span>
              </div>
              {teamNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace(/\//g, "-")}`}
                    className={`sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}
          
          {/* Admin Navigation - only for master admins */}
          {adminNavItems.length > 0 && (
            <>
              <div className="border-t border-slate-800/50 my-2 pt-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 px-3">Admin</span>
              </div>
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.replace(/\//g, "-")}`}
                    className={`sidebar-link ${isActive ? "sidebar-link-active" : ""}`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Section - Compact Design */}
        <div className="sidebar-footer">
          {/* Organization Badge */}
          {user?.organization_name && (
            <Link 
              to="/organization" 
              className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-slate-800/40 hover:bg-slate-800 transition-colors group"
              data-testid="org-link"
            >
              <Building2 className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-slate-300 text-xs truncate flex-1">{user.organization_name}</span>
              <span className="text-slate-500 text-[10px] uppercase tracking-wide">{user.organization_role}</span>
            </Link>
          )}
          
          {!user?.organization_name && (
            <Link 
              to="/organization" 
              className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded border border-dashed border-slate-700/50 hover:border-blue-500/50 hover:bg-slate-800/30 transition-colors"
              data-testid="create-org-link"
            >
              <Building2 className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-slate-500 text-xs">Add Organization</span>
            </Link>
          )}
          
          {/* User Info Row */}
          <div className="flex items-center gap-2 py-2 border-t border-slate-800/50">
            <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-200 text-xs font-medium truncate">{user?.name}</p>
              <p className="text-slate-500 text-[10px] truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded transition-colors"
              title="Sign Out"
            >
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Breadcrumb */}
        <div className="breadcrumb mb-6 flex items-center justify-between" data-testid="breadcrumb">
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="breadcrumb-link flex items-center gap-1">
              <Home className="w-4 h-4" strokeWidth={1.5} />
              <span>Home</span>
            </Link>
            <ChevronRight className="w-4 h-4 breadcrumb-separator" strokeWidth={1.5} />
            <span className="text-slate-900 font-medium">{getBreadcrumbs()}</span>
          </div>
          
          {/* Quick Present Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={enterPresentationMode}
            className="gap-2"
            data-testid="quick-present-btn"
          >
            <Maximize2 className="w-4 h-4" />
            Present
          </Button>
        </div>

        {/* Page Content */}
        <div className="animate-fade-in">
          {children}
        </div>

        {/* Floating Notes Panel */}
        {currentMeeting && <NotesPanel />}
      </main>
    </div>
  );
};

export default Layout;
